import { areEntriesEqual, isEmpty } from '@/helpers/object'
import { getFirstFieldErrorsById } from '@/components/_shared/Form/form-helper'
/**
 * @file Address helpers
 * @author pierre-michel@on-running.com
 *
 * Render addresses (inline or inputs in a form) in the right order relative
 * to the country
 */

const europeanCountries = [
  'ad',
  'at',
  'be',
  'bg',
  'ch',
  'cy',
  'cz',
  'de',
  'dk',
  'ee',
  'es',
  'fi',
  'fr',
  'gr',
  'hu',
  'it',
  'li',
  'lt',
  'lu',
  'lv',
  'mc',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'se',
  'si',
  'sk',
]

const countriesWithStateInSummary = ['ca', 'us', 'mx', 'au']

const countriesWithGoogleAutocompleteDisabled = europeanCountries.concat(['us', 'au', 'nz', 'jp'])

export const getSummary = function(address, { separator = ', ', exclude = [] } = {}) {
  const countryIso = address.countryData?.iso?.toLowerCase()
  const shouldShowState = countriesWithStateInSummary.indexOf(countryIso) >= 0
  const addressObj = {
    fullname: getFullname(address),
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    zipcode: address.zipcode,
    state_id: shouldShowState ? address.stateData?.abbr : null,
    country_id: address.countryData?.name,
  }
  exclude.forEach(key => delete addressObj[key])
  return orderAddressAsArray(addressObj, countryIso).join(separator)
}

/**
 * The different variants that can be used for addresses
 * An example is the AddressMultiline component which is using the variants
 */
export const ADDRESS_VARIANT = {
  long: 'long',
  short: 'short',
}

const addInlineKey = (addressObj, countryIso) => {
  const addressInlineElements = ['city', 'state_id', 'zipcode']
  const inlineCondition = !isJapan(countryIso)
  Object.keys(addressObj).forEach(key => {
    addressObj[key].inline = addressInlineElements.includes(key) && inlineCondition
  })
}

export const getMultilineArray = function(address, variant = ADDRESS_VARIANT.long) {
  const countryIso = address.countryData.iso
  const addressObj = {
    fullname: { key: 'fullname', value: getFullname(address) },
    address1: { key: 'address1', value: address.address1 },
    address2: { key: 'address2', value: address.address2 },
    city: { key: 'city', value: getCity(address, countryIso) },
    state_id: { key: 'state', value: getState(address, countryIso) },
    zipcode: { key: 'zipcode', value: address.zipcode },
    country_id: { key: 'country', value: getCountry(address, variant) },
  }
  addInlineKey(addressObj, countryIso)
  return orderAddressAsArray(addressObj, countryIso)
}

export const getFieldsArray = function(address, country, countryUsedToOrder) {
  const { require_states = true, require_zipcode = true } = country || {}
  const addressObj = {
    gender: { key: 'gender', type: 'select' },
    firstname: { key: 'firstname', type: 'text' },
    lastname: { key: 'lastname', type: 'text' },
    document_number: { key: 'document_number', type: 'text' },
    address1: { key: 'address1', type: 'text' },
    address2: { key: 'address2', type: 'text' },
    neighborhood: { key: 'neighborhood', type: 'text' },
    city: { key: 'city', type: 'text' },
    state_id: { key: 'state_id', type: 'select' },
    zipcode: { key: 'zipcode', type: 'text' },
    country_id: { key: 'country_id', type: 'select' },
    phone: { key: 'phone', type: 'tel' },
  }
  const optionalFieldKeys = getAddressFieldKeysOptional({
    requireStates: require_states,
    requireZipcode: require_zipcode,
  })
  optionalFieldKeys.forEach(key => (addressObj[key].optional = true))
  const countryIso = (countryUsedToOrder || country || { iso: 'us' }).iso
  return orderAddressAsArray(addressObj, countryIso).map(field => {
    field.errors = []
    return field
  })
}

export const areAddressesEqual = (address1, address2, includedProperties = []) => {
  // account and order addresses are not treated the same in the backend.
  // some keys are empty strings where others are null when undefined.
  // therefore we transform all falsy fields to empty string
  const transformProperties = getPermittedFields().reduce((result, key) => {
    result[key] = value => (value ? value : '')
    return result
  }, {})
  const options = {
    // the following properties need to be excluded because
    // "id" > equal addresses will have different ids
    // "is_default" > is only valid for saved addresses
    // "gender" > is only used for saved addresses
    excludedProperties: ['id', 'is_default', 'gender'],
    transformProperties,
    includedProperties,
  }
  return areEntriesEqual(address1, address2, options)
}

export const DEFAULT_NEW_ADDRESS_VALUE = null

export const getDefaultNewAddress = country => {
  // make sure all permitted fields (despite exluded properties) are present to overwrite existing fields when setting default
  const excludedProperties = ['id', 'gender'] // both are not defined in AddressAttributesType
  const defaultAddress = getPermittedFields().reduce((address, fieldKey) => {
    if (!excludedProperties.includes(fieldKey)) address[fieldKey] = DEFAULT_NEW_ADDRESS_VALUE
    return address
  }, {})
  if (!isEmpty(country)) {
    // preselect country
    defaultAddress['country_id'] = country.id
    defaultAddress['countryData'] = country
  }
  return defaultAddress
}

/**
 * Returns an array of optional address field keys.
 *
 * @param {Object} country Current country to use for
 * @param {Boolean} country.requireStates Flag to determine, if the country requires state field to be defined
 * @param {Boolean} country.requireZipcode Flag to determine, if the country requires zipcode field to be defined
 *
 * @returns {Array} list of address field keys to be optional
 */
export const getAddressFieldKeysOptional = function(country) {
  const { requireStates = true, requireZipcode = true } = country || {}
  const optionalFieldKeys = ['gender', 'address2']
  if (!requireStates) optionalFieldKeys.push('state_id')
  if (!requireZipcode) optionalFieldKeys.push('zipcode')
  return optionalFieldKeys
}

export const getPermittedFields = function() {
  return [
    'id',
    'gender',
    'firstname',
    'lastname',
    'address1',
    'address2',
    'zipcode',
    'city',
    'state_id',
    'country_id',
    'phone',
    'neighborhood',
    'document_number',
  ]
}

/**
 * Returns the first error message for each error by id for permitted fields (extracted from object keys).
 *
 * @param {Object} errorsById Object of errors with multiple messages.
 * @param {Object} options Object of options
 * @param {String} options.prefix Optional prefix to pick erros of specific keys (ids could be duplicate e.g. 'bill_address.zipcode' and 'ship_address.zipcode').
 *
 * @returns {Object} First error message for each field
 */
export const getFirstAddressFieldErrorsById = (errorsById, options) => {
  const permittedIds = getPermittedFields()
  return getFirstFieldErrorsById(errorsById, { ...options, permittedIds })
}

/**
 * Returns all error messages for each error by id.
 *
 * @param {Object} errorsById Object of errors with multiple messages.
 *
 * @returns {Array} flattended list of all error messages
 */
export const getErrorsMessages = errorsById => {
  return Object.values(errorsById).flat()
}

/**
 * Returns a list of address field keys in country specific order.
 *
 * @param {Object} country Current country used to determine, if an address field is rendered with half width
 * @param {String} country.iso Country iso (e.g. "us")
 *
 * @returns {Array} list of address field keys in country specific order
 */
export const getAddressFieldKeysOrder = function(country) {
  const { iso = '' } = country || {}
  if (isJapan(iso)) {
    return getAddressFieldKeysOrderForJapan()
  } else if (isEuropeanCountry(iso)) {
    return getAddressFieldKeysOrderForEU()
  } else {
    return getDefaultAddressFieldKeysOrder()
  }
}

/**
 * Returns a list of address field keys that are rendered only in half of the width.
 * IMPORTANT: Half width fields rely on the order and should be next to each other (see below).
 *
 * @param {Object} country Current country used to determine, if an address field is rendered with half width
 * @param {String} country.iso Country iso (e.g. "us")
 * @param {Boolean} country.hasStates Does country has states
 *
 * @returns {Array} list of address field keys rendered only with half of the width
 */
export const getAddressFieldKeysHalfWidth = function(country) {
  const { iso = '', hasStates = true } = country || {}
  const fieldKeysHalfWidth = ['firstname', 'lastname']
  if (isEuropeanCountry(iso)) {
    return [...fieldKeysHalfWidth, 'zipcode', 'city']
  } else if (hasStates) {
    return [...fieldKeysHalfWidth, 'state_id', 'zipcode']
  } else {
    return [...fieldKeysHalfWidth, 'country_id', 'zipcode']
  }
}

/**
 * Returns a list of address field keys to be hidden by country in address form.
 *
 * @param {Object} country Current country to use for hiding
 * @param {String} country.iso Country iso (e.g. "us")
 * @param {Boolean} country.hasStates Flag to determine, if this country has states
 *
 * @returns {Array} list of address field keys to be hidden
 */
export const getAddressFieldKeysHidden = function(country) {
  const { iso = '', hasStates = true } = country || {}
  const hiddenFields = []
  if (!isBrazil(iso)) {
    hiddenFields.push(...['document_number', 'neighborhood'])
  }
  if (isExplicitlyFalse(hasStates)) {
    hiddenFields.push('state_id')
  }
  return hiddenFields
}

const isExplicitlyFalse = function(value) {
  return typeof value !== 'undefined' && value === false
}

export const isUs = function(countryIso) {
  return /^us/i.test(countryIso)
}

export const isJapan = function(countryIso) {
  return /^jp/i.test(countryIso)
}

const isBrazil = function(countryIso) {
  return /^br$/.test(countryIso)
}

export const isEuropeanCountry = function(countryIso) {
  return europeanCountries.includes(countryIso)
}

export const isGoogleAutocompleteDisabled = function(countryIso) {
  return countriesWithGoogleAutocompleteDisabled.includes(countryIso)
}

const orderAddressAsArray = function(addressObj, countryIso) {
  const ordered = orderAddressObject(addressObj, countryIso)
  return Object.entries(ordered).map(entry => entry[1])
}

const orderAddressObject = function(addressObj, countryIso) {
  const ordered = {}
  getAddressFieldKeysOrder({ iso: countryIso }).forEach(key => {
    if (addressObj[key]) {
      ordered[key] = addressObj[key]
    }
  })
  return ordered
}

export const getFullname = function({ firstname, lastname, countryData }) {
  const countryIso = countryData?.iso?.toLowerCase()
  return isJapan(countryIso) ? `${lastname} ${firstname}` : `${firstname} ${lastname}`
}

const getCity = (address, countryIso) => {
  const isOtherCountry = !isJapan(countryIso) && !isEuropeanCountry(countryIso)
  return `${address.city}${isOtherCountry ? ',' : ''}`
}

const getState = (address, countryIso) => {
  return address.stateData?.abbr && !isEuropeanCountry(countryIso)
    ? `${address.stateData.abbr}${!isJapan(countryIso) ? ' ' : ''}`
    : ''
}

const getCountry = (address, variant) => {
  return variant === ADDRESS_VARIANT.long ? address.countryData.name : ''
}

/**
 * IMPORTANT: Half width fields rely on this order and should be next to each other (see above).
 */
const getAddressFieldKeysOrderForJapan = function() {
  return [
    'gender',
    'fullname',
    'lastname',
    'firstname',
    'document_number',
    'country_id', // half width (if no states present)
    'zipcode', // half width
    'state_id', // half width
    'city',
    'neighborhood',
    'address1',
    'address2',
    'phone',
  ]
}

/**
 * IMPORTANT: Half width fields rely on this order and should be next to each other (see above).
 */
const getAddressFieldKeysOrderForEU = function() {
  return [
    'gender',
    'fullname',
    'firstname',
    'lastname',
    'document_number',
    'address1',
    'address2',
    'neighborhood',
    'zipcode', // half width
    'city', // half width
    'state_id',
    'country_id',
    'phone',
  ]
}

/**
 * IMPORTANT: Half width fields rely on this order and should be next to each other (see above).
 */
const getDefaultAddressFieldKeysOrder = function() {
  return [
    'gender',
    'fullname',
    'firstname',
    'lastname',
    'document_number',
    'address1',
    'address2',
    'neighborhood',
    'city',
    'state_id', // half width
    'zipcode', // half width
    'country_id', // half width (if no states present)
    'phone',
  ]
}
